import {
  Badge,
  DataTable,
  DescriptionList,
  InputField,
  Modal,
  RichTextEditor,
  Select,
  type DataTableColumn,
  type DataTableFooterRow,
  type DataTableRow,
  type SelectOption,
} from '@enamelai/design-system'
import { BALANCE_BADGE_COLOR } from '@constants/Billing'
import {
  BalanceBadgeStatus,
  BillingProcedure,
  LedgerEntry,
} from '@dataTypes/billing.types'
import { formatCurrency, readSelectValue } from '@utils/billing.utils'
import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import useApplyAdjustmentModal from './useApplyAdjustmentModal.hook'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import './ApplyAdjustmentModal.css'

const noteContent = (notes: Array<Record<string, unknown>>): string => {
  const content = notes[0]?.content
  return typeof content === 'string' ? content : ''
}

const hasNoteText = (value: string): boolean =>
  value
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim().length > 0

const readAmountValue = (value: string): string => value.replace(/,/g, '')

interface Props {
  isVisible: boolean
  closeModal: () => void
  ledgerEntry: LedgerEntry | null
}

const ApplyAdjustmentModal: React.FC<Props> = ({
  isVisible,
  closeModal,
  ledgerEntry,
}) => {
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const hooks = useApplyAdjustmentModal(
    ledgerEntry,
    isVisible,
    closeModal,
    showToast,
    showLoading,
    hideLoading,
  )
  const originalProcedures = useMemo(
    () => ledgerEntry?.procedures || [],
    [ledgerEntry],
  )

  const insuranceDropdownOptions = useMemo<SelectOption[]>(
    () =>
      hooks.insuranceAdjustmentTypes.map((type) => ({
        value: type.adjustment_type_id,
        label: type.name,
        trailingText: type.modifier === 'Positive' ? '(+)' : '(-)',
      })),
    [hooks.insuranceAdjustmentTypes],
  )

  const patientDropdownOptions = useMemo<SelectOption[]>(
    () =>
      hooks.patientAdjustmentTypes.map((type) => ({
        value: type.adjustment_type_id,
        label: type.name,
        trailingText: type.modifier === 'Positive' ? '(+)' : '(-)',
      })),
    [hooks.patientAdjustmentTypes],
  )

  const formatProcedureCode = (_: unknown, row: BillingProcedure): string =>
    row.procedureCode

  const formatCharges = (
    _: unknown,
    row: BillingProcedure,
  ): React.ReactNode => (
    <span>{row.clinicFee !== null ? formatCurrency(row.clinicFee) : '-'}</span>
  )

  const formatApprovedInsurance = (
    _: unknown,
    row: BillingProcedure,
  ): React.ReactNode => {
    const approvedInsurance = row.approvedInsurance ?? 0
    const rowInsuranceBalance = row.insuranceBalance ?? 0
    const originalProcedure = originalProcedures.find(
      (procedure) => procedure.procedureLogId === row.procedureLogId,
    )
    const insuranceCashAndAdjustment =
      (originalProcedure?.insuranceCash ?? 0) +
      (originalProcedure?.insuranceAdjustment ?? 0)
    const insuranceStatus =
      insuranceCashAndAdjustment > 0 && rowInsuranceBalance !== 0
        ? BalanceBadgeStatus.Partial
        : rowInsuranceBalance === 0 && insuranceCashAndAdjustment > 0
          ? BalanceBadgeStatus.Paid
          : BalanceBadgeStatus.Unpaid
    return (
      <div className="d-flex align-items-center gap-2">
        <span>{formatCurrency(approvedInsurance)}</span>
        <Badge
          mode="tinted"
          color={BALANCE_BADGE_COLOR[insuranceStatus]}
          label={formatCurrency(rowInsuranceBalance)}
        />
      </div>
    )
  }

  const formatInsuranceAdjustment = (
    _: unknown,
    row: BillingProcedure,
  ): React.ReactNode => {
    const draft =
      hooks.adjustmentDrafts[row.procedureLogId]?.insuranceAdjustment
    const adjustment =
      draft !== undefined
        ? draft
        : row.insuranceAdjustment !== undefined &&
            row.insuranceAdjustment !== null
          ? row.insuranceAdjustment
          : ''

    return (
      <InputField
        variant="amount"
        fullWidth
        label=""
        aria-label="Insurance adjustment"
        className="adjustment-amount-field"
        value={adjustment}
        disabled={!hooks.insuranceAdjustmentType}
        onChange={(event) =>
          hooks.handleAdjustmentChange(
            row.procedureLogId,
            'insuranceAdjustment',
            readAmountValue(event.target.value),
          )
        }
        onBlur={(event) =>
          hooks.handleAdjustmentBlur(
            row.procedureLogId,
            'insuranceAdjustment',
            readAmountValue(event.target.value),
          )
        }
      />
    )
  }

  const formatPatientResponsibility = (
    _: unknown,
    row: BillingProcedure,
  ): React.ReactNode => {
    const patientResponsibility = row.patientResponsibility ?? 0
    const rowPatientBalance = row.patientBalance ?? 0
    const originalProcedure = originalProcedures.find(
      (procedure) => procedure.procedureLogId === row.procedureLogId,
    )
    const patientCashAndAdjustment =
      (originalProcedure?.patientCash ?? 0) +
      (originalProcedure?.patientAdjustment ?? 0)
    const patientStatus =
      patientCashAndAdjustment > 0 && rowPatientBalance !== 0
        ? BalanceBadgeStatus.Partial
        : rowPatientBalance === 0 && patientCashAndAdjustment > 0
          ? BalanceBadgeStatus.Paid
          : BalanceBadgeStatus.Unpaid
    return (
      <div className="d-flex align-items-center gap-2">
        <span>{formatCurrency(patientResponsibility)}</span>
        <Badge
          mode="tinted"
          color={BALANCE_BADGE_COLOR[patientStatus]}
          label={formatCurrency(rowPatientBalance)}
        />
      </div>
    )
  }

  const formatPatientAdjustment = (
    _: unknown,
    row: BillingProcedure,
  ): React.ReactNode => {
    const draft = hooks.adjustmentDrafts[row.procedureLogId]?.patientAdjustment
    const adjustment =
      draft !== undefined
        ? draft
        : row.patientAdjustment !== undefined && row.patientAdjustment !== null
          ? row.patientAdjustment
          : ''

    return (
      <InputField
        variant="amount"
        fullWidth
        label=""
        aria-label="Patient adjustment"
        className="adjustment-amount-field"
        value={adjustment}
        disabled={!hooks.patientAdjustmentType}
        onChange={(event) =>
          hooks.handleAdjustmentChange(
            row.procedureLogId,
            'patientAdjustment',
            readAmountValue(event.target.value),
          )
        }
        onBlur={(event) =>
          hooks.handleAdjustmentBlur(
            row.procedureLogId,
            'patientAdjustment',
            readAmountValue(event.target.value),
          )
        }
      />
    )
  }

  const proceduresById = new Map(
    hooks.procedures.map((procedure) => [procedure.procedureLogId, procedure]),
  )
  const procedureRows: DataTableRow[] = hooks.procedures.map((procedure) => ({
    id: procedure.procedureLogId,
  }))
  const renderProcedureCell = (
    row: DataTableRow,
    render: (procedure: BillingProcedure) => React.ReactNode,
  ): React.ReactNode => {
    const procedure = proceduresById.get(row.id)
    return procedure ? render(procedure) : null
  }
  const procedureColumns: DataTableColumn[] = [
    {
      id: 'procedureCode',
      label: 'Code',
      renderCell: (row) =>
        renderProcedureCell(row, (procedure) =>
          formatProcedureCode(undefined, procedure),
        ),
    },
    {
      id: 'clinicFee',
      label: 'Charges',
      renderCell: (row) =>
        renderProcedureCell(row, (procedure) =>
          formatCharges(undefined, procedure),
        ),
    },
    {
      id: 'approvedInsurance',
      label: 'Approved ins.',
      renderCell: (row) =>
        renderProcedureCell(row, (procedure) =>
          formatApprovedInsurance(undefined, procedure),
        ),
    },
    {
      id: 'insuranceAdjustment',
      label: 'Insurance adj.',
      renderCell: (row) =>
        renderProcedureCell(row, (procedure) =>
          formatInsuranceAdjustment(undefined, procedure),
        ),
    },
    {
      id: 'patientResponsibility',
      label: 'Patient resp.',
      renderCell: (row) =>
        renderProcedureCell(row, (procedure) =>
          formatPatientResponsibility(undefined, procedure),
        ),
    },
    {
      id: 'patientAdjustment',
      label: 'Patient adj.',
      renderCell: (row) =>
        renderProcedureCell(row, (procedure) =>
          formatPatientAdjustment(undefined, procedure),
        ),
    },
  ]

  const footerRow = useMemo((): DataTableFooterRow => {
    const {
      clinicFee,
      approvedInsurance,
      insuranceAdjustment,
      patientResponsibility,
      patientAdjustment,
    } = hooks.procedureTotals

    const insuranceCashAndAdjustment = originalProcedures.reduce(
      (sum, procedure) =>
        sum +
        (procedure.insuranceCash ?? 0) +
        (procedure.insuranceAdjustment ?? 0),
      0,
    )
    const insuranceStatus =
      insuranceCashAndAdjustment > 0 && hooks.insuranceBalance !== 0
        ? BalanceBadgeStatus.Partial
        : hooks.insuranceBalance === 0 && insuranceCashAndAdjustment > 0
          ? BalanceBadgeStatus.Paid
          : BalanceBadgeStatus.Unpaid

    const insuranceAdjustmentTypeObj = hooks.insuranceAdjustmentTypes.find(
      (type) => type.adjustment_type_id === hooks.insuranceAdjustmentType,
    )
    const insuranceAdjustmentPrefix =
      hooks.insuranceAdjustmentType && insuranceAdjustment !== 0
        ? insuranceAdjustmentTypeObj?.modifier === 'Negative'
          ? '-'
          : '+'
        : ''

    const patientCashAndAdjustment = originalProcedures.reduce(
      (sum, procedure) =>
        sum + (procedure.patientCash ?? 0) + (procedure.patientAdjustment ?? 0),
      0,
    )
    const patientStatus =
      patientCashAndAdjustment > 0 && hooks.patientBalance !== 0
        ? BalanceBadgeStatus.Partial
        : hooks.patientBalance === 0 && patientCashAndAdjustment > 0
          ? BalanceBadgeStatus.Paid
          : BalanceBadgeStatus.Unpaid

    const patientAdjustmentTypeObj = hooks.patientAdjustmentTypes.find(
      (type) => type.adjustment_type_id === hooks.patientAdjustmentType,
    )
    const patientAdjustmentPrefix =
      hooks.patientAdjustmentType && patientAdjustment !== 0
        ? patientAdjustmentTypeObj?.modifier === 'Negative'
          ? '-'
          : '+'
        : ''

    return {
      procedureCode: 'Total',
      clinicFee: formatCurrency(clinicFee),
      approvedInsurance: (
        <div className="d-flex align-items-center gap-2">
          <span>{formatCurrency(approvedInsurance)}</span>
          <Badge
            mode="tinted"
            color={BALANCE_BADGE_COLOR[insuranceStatus]}
            label={formatCurrency(hooks.insuranceBalance)}
          />
        </div>
      ),
      insuranceAdjustment: insuranceAdjustmentPrefix
        ? `${insuranceAdjustmentPrefix}${formatCurrency(insuranceAdjustment)}`
        : formatCurrency(insuranceAdjustment),
      patientResponsibility: (
        <div className="d-flex align-items-center gap-2">
          <span>{formatCurrency(patientResponsibility)}</span>
          <Badge
            mode="tinted"
            color={BALANCE_BADGE_COLOR[patientStatus]}
            label={formatCurrency(hooks.patientBalance)}
          />
        </div>
      ),
      patientAdjustment: patientAdjustmentPrefix
        ? `${patientAdjustmentPrefix}${formatCurrency(patientAdjustment)}`
        : formatCurrency(patientAdjustment),
    }
  }, [
    hooks.procedureTotals,
    hooks.insuranceAdjustmentType,
    hooks.patientAdjustmentType,
    hooks.insuranceAdjustmentTypes,
    hooks.patientAdjustmentTypes,
    hooks.insuranceBalance,
    hooks.patientBalance,
    originalProcedures,
  ])

  if (!ledgerEntry) return null

  return (
    <Modal
      title="Apply adjustment"
      open={isVisible}
      onClose={closeModal}
      size="lg"
      closeOnBackdrop={false}
      bodyClassName="adjustment-modal-body"
      primaryLabel="Apply"
      secondaryLabel="Cancel"
      primaryDisabled={hooks.loading}
      onPrimaryClick={hooks.handleApply}
      onSecondaryClick={closeModal}
    >
      <Row className="mb-3 gx-2">
        <Col md={6}>
          <Select
            label="Insurance adj. type"
            value={hooks.insuranceAdjustmentType}
            options={insuranceDropdownOptions}
            onChange={(value) =>
              hooks.setInsuranceAdjustmentType(readSelectValue(value))
            }
            placeholder="Select type"
          />
        </Col>
        <Col md={6}>
          <Select
            label="Patient adj. type"
            value={hooks.patientAdjustmentType}
            options={patientDropdownOptions}
            onChange={(value) =>
              hooks.setPatientAdjustmentType(readSelectValue(value))
            }
            placeholder="Select type"
          />
        </Col>
      </Row>

      <div>
        <DataTable
          columns={procedureColumns}
          rows={procedureRows}
          footerRow={footerRow}
          showActions={false}
        />
      </div>

      <DescriptionList
        className="mt-3"
        variant="file-details"
        columns={1}
        items={[
          {
            label: 'Charges',
            value: formatCurrency(hooks.summaryValues.charges),
          },
          {
            label: 'Appointment adjustment',
            value: formatCurrency(hooks.summaryValues.appointmentAdjustment),
          },
          {
            label: 'Total appointment balance',
            value: formatCurrency(hooks.summaryValues.totalAppointmentBalance),
          },
        ]}
      />

      <Row className="mt-4 mb-3">
        <Col md={12}>
          <RichTextEditor
            variant="sms-notes"
            value={noteContent(hooks.notes)}
            placeholder="Add a note"
            bodyActions={[]}
            footerActions={[]}
            aiEnabled={false}
            onChange={(value) => {
              if (!hasNoteText(value)) {
                hooks.handleUpdateNotes([])
                return
              }
              hooks.handleUpdateNotes([
                { ...(hooks.notes[0] ?? {}), content: value },
              ])
            }}
          />
        </Col>
      </Row>
    </Modal>
  )
}

export default ApplyAdjustmentModal
